import React, { createContext, useContext, useReducer, useEffect } from 'react'
import { supabase } from '../config/supabase'
import { favoriteService, trackService, userService } from '../services/supabaseService'

const MusicContext = createContext()

const initialState = {
  currentTrack: null,
  playlist: [],
  isPlaying: false,
  currentTime: 0,
  duration: 0,
  volume: 1,
  mood: '',
  weather: null,
  location: null,
  favorites: [],
  recommendations: [],
  user: null,
  isLoading: false,
  error: null,
  currentSession: null
}

function musicReducer(state, action) {
  switch (action.type) {
    case 'SET_CURRENT_TRACK':
      return { ...state, currentTrack: action.payload }
    case 'SET_PLAYLIST':
      return { ...state, playlist: action.payload }
    case 'SET_PLAYING':
      return { ...state, isPlaying: action.payload }
    case 'SET_CURRENT_TIME':
      return { ...state, currentTime: action.payload }
    case 'SET_DURATION':
      return { ...state, duration: action.payload }
    case 'SET_VOLUME':
      return { ...state, volume: action.payload }
    case 'SET_MOOD':
      return { ...state, mood: action.payload }
    case 'SET_WEATHER':
      return { ...state, weather: action.payload }
    case 'SET_LOCATION':
      return { ...state, location: action.payload }
    case 'ADD_FAVORITE':
      return { 
        ...state, 
        favorites: [...state.favorites, action.payload] 
      }
    case 'REMOVE_FAVORITE':
      return { 
        ...state, 
        favorites: state.favorites.filter(fav => fav.id !== action.payload) 
      }
    case 'SET_RECOMMENDATIONS':
      return { ...state, recommendations: action.payload }
    case 'SET_USER':
      return { ...state, user: action.payload }
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload }
    case 'SET_ERROR':
      return { ...state, error: action.payload }
    case 'SET_CURRENT_SESSION':
      return { ...state, currentSession: action.payload }
    case 'SET_FAVORITES':
      return { ...state, favorites: action.payload }
    case 'NEXT_TRACK':
      const currentIndex = state.playlist.findIndex(track => track.id === state.currentTrack?.id)
      const nextIndex = (currentIndex + 1) % state.playlist.length
      return { ...state, currentTrack: state.playlist[nextIndex] }
    case 'PREVIOUS_TRACK':
      const prevIndex = state.playlist.findIndex(track => track.id === state.currentTrack?.id)
      const previousIndex = prevIndex > 0 ? prevIndex - 1 : state.playlist.length - 1
      return { ...state, currentTrack: state.playlist[previousIndex] }
    default:
      return state
  }
}

export function MusicProvider({ children }) {
  const [state, dispatch] = useReducer(musicReducer, initialState)

  // 初始化认证状态和数据
  useEffect(() => {
    initializeAuth()
  }, [])

  // 监听用户变化，同步收藏数据
  useEffect(() => {
    if (state.user) {
      loadUserFavorites()
    } else {
      // 如果用户未登录，从 localStorage 加载收藏
      loadLocalFavorites()
    }
  }, [state.user])

  // 保存收藏（根据用户状态决定保存到数据库还是本地）
  useEffect(() => {
    if (state.user) {
      // 如果用户已登录，不需要保存到 localStorage
      // 收藏操作直接通过 API 调用处理
    } else {
      // 未登录用户，保存到 localStorage
      localStorage.setItem('music-favorites', JSON.stringify(state.favorites))
    }
  }, [state.favorites, state.user])

  // 初始化认证状态
  const initializeAuth = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (session?.user) {
        dispatch({ type: 'SET_USER', payload: session.user })
      }
    } catch (error) {
      console.error('Error initializing auth:', error)
    }

    // 监听认证状态变化
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        dispatch({ type: 'SET_USER', payload: session?.user || null })
        if (event === 'SIGNED_OUT') {
          dispatch({ type: 'SET_FAVORITES', payload: [] })
        }
      }
    )

    return () => subscription.unsubscribe()
  }

  // 加载用户收藏
  const loadUserFavorites = async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true })
      const favorites = await favoriteService.getUserFavorites(state.user.id)
      dispatch({ type: 'SET_FAVORITES', payload: favorites })
    } catch (error) {
      console.error('Error loading user favorites:', error)
      dispatch({ type: 'SET_ERROR', payload: 'Failed to load favorites' })
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false })
    }
  }

  // 从本地存储加载收藏
  const loadLocalFavorites = () => {
    try {
      const savedFavorites = localStorage.getItem('music-favorites')
      if (savedFavorites) {
        const favorites = JSON.parse(savedFavorites)
        dispatch({ type: 'SET_FAVORITES', payload: favorites })
      }
    } catch (error) {
      console.error('Error loading local favorites:', error)
    }
  }

  // 收藏相关函数
  const addFavorite = async (track) => {
    try {
      // 先保存曲目到数据库
      await trackService.saveTrack(track)
      
      if (state.user) {
        // 用户已登录，添加到数据库
        await favoriteService.addToFavorites(state.user.id, track.id)
        // 重新加载收藏列表
        await loadUserFavorites()
      } else {
        // 用户未登录，添加到本地
        dispatch({ type: 'ADD_FAVORITE', payload: track })
      }
    } catch (error) {
      console.error('Error adding favorite:', error)
      dispatch({ type: 'SET_ERROR', payload: 'Failed to add favorite' })
    }
  }

  const removeFavorite = async (trackId) => {
    try {
      if (state.user) {
        // 用户已登录，从数据库删除
        await favoriteService.removeFromFavorites(state.user.id, trackId)
        // 重新加载收藏列表
        await loadUserFavorites()
      } else {
        // 用户未登录，从本地删除
        dispatch({ type: 'REMOVE_FAVORITE', payload: trackId })
      }
    } catch (error) {
      console.error('Error removing favorite:', error)
      dispatch({ type: 'SET_ERROR', payload: 'Failed to remove favorite' })
    }
  }

  const isFavorite = (trackId) => {
    return state.favorites.some(fav => fav.id === trackId)
  }

  // 认证相关函数
  const signIn = async (email, password) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true })
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      })
      if (error) throw error
      return data
    } catch (error) {
      console.error('Sign in error:', error)
      dispatch({ type: 'SET_ERROR', payload: error.message })
      throw error
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false })
    }
  }

  const signUp = async (email, password, metadata = {}) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true })
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: metadata
        }
      })
      if (error) throw error
      return data
    } catch (error) {
      console.error('Sign up error:', error)
      dispatch({ type: 'SET_ERROR', payload: error.message })
      throw error
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false })
    }
  }

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut()
      if (error) throw error
    } catch (error) {
      console.error('Sign out error:', error)
      dispatch({ type: 'SET_ERROR', payload: error.message })
    }
  }

  const value = {
    ...state,
    dispatch,
    // 播放控制函数
    play: () => dispatch({ type: 'SET_PLAYING', payload: true }),
    pause: () => dispatch({ type: 'SET_PLAYING', payload: false }),
    togglePlay: () => dispatch({ type: 'SET_PLAYING', payload: !state.isPlaying }),
    nextTrack: () => dispatch({ type: 'NEXT_TRACK' }),
    previousTrack: () => dispatch({ type: 'PREVIOUS_TRACK' }),
    setCurrentTrack: (track) => dispatch({ type: 'SET_CURRENT_TRACK', payload: track }),
    setPlaylist: (playlist) => dispatch({ type: 'SET_PLAYLIST', payload: playlist }),
    // 收藏函数
    addFavorite,
    removeFavorite,
    isFavorite,
    // 认证函数
    signIn,
    signUp,
    signOut,
    // 数据加载函数
    loadUserFavorites,
    loadLocalFavorites,
    // 错误清除
    clearError: () => dispatch({ type: 'SET_ERROR', payload: null })
  }

  return (
    <MusicContext.Provider value={value}>
      {children}
    </MusicContext.Provider>
  )
}

export function useMusic() {
  const context = useContext(MusicContext)
  if (!context) {
    throw new Error('useMusic must be used within a MusicProvider')
  }
  return context
}
